import { computed, signal } from '@preact/signals-react'
import type {
  EmulatedControllerId,
  EmulatedControllerInfo,
  GamepadEmulation,
} from './gamepadEmulation.types'
import { getMerelsGamepadEmulationInstallScript } from './injectedScript'

function controllerIdToIndex(controllerId: EmulatedControllerId): number {
  // X1 -> 0, X2 -> 1, X3 -> 2
  return Number(controllerId.slice(1)) - 1
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true
  return Boolean((el as any).isContentEditable)
}

export function makeGamepadEmulation(): GamepadEmulation {
  const isEnabled = signal<boolean>(false)
  const selectedControllerId = signal<EmulatedControllerId | 'K' | null>(null)

  const controllers = signal<EmulatedControllerInfo[]>([
    { id: 'X1', label: 'Red Controller', index: 0, connected: false },
    { id: 'X2', label: 'Blue Controller', index: 1, connected: false },
    { id: 'X3', label: 'Lime Controller', index: 2, connected: false },
  ])

  type BrowserEmulationApi = {
    __installed: boolean
    enable(): void
    disable(): void
    ensureSlotCount(count: number): void
    connect(index: number, id: string): void
    disconnect(index: number): void
    pulseButton(index: number, buttonIndex: number, durationMs?: number): void
    setLeftStick(index: number, x: number, y: number): void
    resetLeftStick(index: number): void
  }

  function ensureBrowserApi(): BrowserEmulationApi | null {
    if (typeof window === 'undefined') return null
    const existing = (window as any).__merelsGamepadEmulation as BrowserEmulationApi | undefined
    if (existing && existing.__installed) return existing
    const script = getMerelsGamepadEmulationInstallScript()
    // Execute in current JS context.
    // eslint-disable-next-line no-new-func
    new Function(script)()
    const after = (window as any).__merelsGamepadEmulation as BrowserEmulationApi | undefined
    return after && after.__installed ? after : null
  }

  function persist(): void {
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage.setItem('merels.gamepadEmulation.enabled', String(isEnabled.value))
      window.sessionStorage.setItem('merels.gamepadEmulation.selected', selectedControllerId.value ?? '')
      window.sessionStorage.setItem(
        'merels.gamepadEmulation.connected',
        JSON.stringify(controllers.value.map((c) => ({ id: c.id, connected: c.connected })))
      )
    } catch {
      // Ignore persistence failures (e.g., disabled storage).
    }
  }

  function restoreFromStorage(): void {
    if (typeof window === 'undefined') return
    try {
      const enabled = window.sessionStorage.getItem('merels.gamepadEmulation.enabled')
      const selected = window.sessionStorage.getItem('merels.gamepadEmulation.selected')
      // keyboard routing is derived from selection; no separate flag.
      const connectedRaw = window.sessionStorage.getItem('merels.gamepadEmulation.connected')

      if (enabled === 'true') isEnabled.value = true
      if (selected === 'X1' || selected === 'X2' || selected === 'X3' || selected === 'K') selectedControllerId.value = selected

      if (connectedRaw) {
        const parsed = JSON.parse(connectedRaw) as Array<{ id: EmulatedControllerId; connected: boolean }>
        const nextControllers = controllers.value.map((c) => {
          const match = parsed.find((p) => p.id === c.id)
          return match ? { ...c, connected: Boolean(match.connected) } : c
        })
        controllers.value = nextControllers
      }
    } catch {
      // Ignore parse/storage errors.
    }
  }

  function enable(): void {
    const api = ensureBrowserApi()
    // Keep enable idempotent: restoring from storage sets isEnabled first, so we must
    // still ensure the browser patch is installed and connections are replayed.
    isEnabled.value = true
    api?.enable()
    api?.ensureSlotCount(3)
    // Replay connected controllers into browser API.
    for (const c of controllers.value) {
      if (!c.connected) continue
      api?.connect(c.index, c.label)
    }
    persist()
  }

  function disable(): void {
    if (!isEnabled.value) return
    const api = ensureBrowserApi()
    api?.disable()

    isEnabled.value = false
    // Keep selection, but allow "no selection" for header indicator if user never picked one.
    controllers.value = controllers.value.map((c) => ({ ...c, connected: false }))
    persist()
  }

  function setSelected(next: EmulatedControllerId): void {
    selectedControllerId.value = next
    persist()
  }

  function toggleSelectedConnected(): void {
    const selected = selectedControllerId.value
    if (!selected) return
    if (selected === 'K') return
    const info = controllers.value.find((c) => c.id === selected) || null
    if (info?.connected) {
      disconnect(selected)
      return
    }
    connect(selected)
  }

  function connect(controllerId: EmulatedControllerId): void {
    if (!isEnabled.value) enable()
    const api = ensureBrowserApi()
    const idx = controllerIdToIndex(controllerId)
    const c = controllers.value.find((x) => x.id === controllerId)
    if (!c) return
    if (c.connected) return

    controllers.value = controllers.value.map((x) => (x.id === controllerId ? { ...x, connected: true } : x))
    api?.ensureSlotCount(3)
    api?.connect(idx, c.label)
    persist()
  }

  function disconnect(controllerId: EmulatedControllerId): void {
    if (!isEnabled.value) return
    const api = ensureBrowserApi()
    const idx = controllerIdToIndex(controllerId)
    const c = controllers.value.find((x) => x.id === controllerId)
    if (!c) return
    if (!c.connected) return

    controllers.value = controllers.value.map((x) => (x.id === controllerId ? { ...x, connected: false } : x))
    api?.disconnect(idx)
    persist()
  }

  function disconnectAll(): void {
    disconnect('X1')
    disconnect('X2')
    disconnect('X3')
  }

  function pulseButton(controllerId: EmulatedControllerId, buttonIndex: number, durationMs = 50): void {
    if (!isEnabled.value) return
    const api = ensureBrowserApi()
    const idx = controllerIdToIndex(controllerId)
    api?.pulseButton(idx, buttonIndex, durationMs)
  }

  function setLeftStick(controllerId: EmulatedControllerId, x: number, y: number): void {
    if (!isEnabled.value) return
    const api = ensureBrowserApi()
    const idx = controllerIdToIndex(controllerId)
    api?.setLeftStick(idx, x, y)
  }

  function resetLeftStick(controllerId: EmulatedControllerId): void {
    if (!isEnabled.value) return
    const api = ensureBrowserApi()
    const idx = controllerIdToIndex(controllerId)
    api?.resetLeftStick(idx)
  }

  // Wire keyboard routing for controllerInput.
  const keyboardTargetControllerId = computed<EmulatedControllerId | null>(() => {
    if (!isEnabled.value) return null
    const selected = selectedControllerId.value
    if (selected === 'X1' || selected === 'X2' || selected === 'X3') return selected
    return null
  })

  // One-time storage restore (does not enable patching).
  restoreFromStorage()
  if (isEnabled.value) enable()

  // Global shortcuts:
  // - 1/2/3: select X1/X2/X3
  // - 0: select keyboard ("K")
  // - =: connect/disconnect selected controller
  if (typeof window !== 'undefined') {
    window.addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        if (isTypingTarget(e.target)) return

        // Let browser shortcuts pass through (Cmd+1, Cmd+2, Cmd+3, etc. for tab switching)
        if ((e.metaKey || e.ctrlKey) && /^[0-9]$/.test(e.key)) {
          return
        }

        if (e.key === '0') {
          e.preventDefault()
          selectedControllerId.value = 'K'
          persist()
          return
        }
        if (e.key === '1') {
          e.preventDefault()
          setSelected('X1')
          return
        }
        if (e.key === '2') {
          e.preventDefault()
          setSelected('X2')
          return
        }
        if (e.key === '3') {
          e.preventDefault()
          setSelected('X3')
          return
        }
        if (e.key === '=') {
          e.preventDefault()
          // If nothing is selected yet, default to X1 for toggle.
          if (selectedControllerId.value === null) setSelected('X1')
          if (selectedControllerId.value === 'K') return
          toggleSelectedConnected()
          return
        }
      },
      true
    )
  }

  return {
    isEnabled,
    selectedControllerId,
    controllers,
    enable,
    disable,
    connect,
    disconnect,
    disconnectAll,
    pulseButton,
    setLeftStick,
    resetLeftStick,
    keyboardWriter: {
      targetControllerId: keyboardTargetControllerId,
      pulseButton,
    },
  }
}


